import Icon from "@mdi/react";
import { mdiCalendar, mdiPlus } from "@mdi/js";
import { Link } from "easy-page-router/react";

import { Footer, Header } from "../../Layout";
import { useHead } from "../../services/common";
import { ContentSwitch, Loading, OtherPolls, Page, VoteResult, When } from "../../components";
import { ThemeDiv, ThemeSwitch } from "../../components/Theme";
import { LangSwitch } from "../../components/Lang";
import { useLang } from "../../services/lang";
import { useLocalHistory } from "../../services/localStorage";
import { useApi, useHandleError } from "../../services/api";
import { WhenPreview } from "../../../types";
import { Votes } from "../../components/Vote";
import { useEffect, useState } from "react";
import { useSSRHook } from "ssr-hook";
import LaunchButton from "../../components/LaunchButton";


export default function WhenBoard() {
    const { dict } = useLang();

    useHead({
        title: dict.seo.whenBoard.title,
        description: dict.seo.whenBoard.description,
    });

    const [contentIndex, setContentIndex] = useState(1);

    return <Page fixed={<>
        <Header title={dict.when} link={`/${dict.urlWhen}`}>
            <LangSwitch />
            <ThemeSwitch />
        </Header>
        <Footer />
    </>}>

        <h1>{dict.when} <span className="sub-title">{dict.whenSubTitle}</span></h1>

        <p className="page-description">{dict.seo.whenBoard.text}</p>

        <Link className="button-new" href={`/${dict.urlWhen}/${dict.urlNew}`}>
            <Icon path={mdiPlus} size={1} /> {dict.newWhen} <Icon path={mdiCalendar} size={1} />
        </Link>

        <OtherPolls what who />

        <ContentSwitch
            index={contentIndex}
            onChange={setContentIndex}
            options={[{
                title: dict.pollsLocal,
                content: <Local onLoad={() => setContentIndex(0)} />,
            }, {
                title: dict.pollsPublic,
                content: <Public />,
            }]}
        />

    </Page>;
}


function Local({ onLoad }: { onLoad: () => void }) {
    const whenIds = useLocalHistory("when");
    const [whens, error, isLoading, reload] = useApi.getWhenPreviews({ whenIds });
    useHandleError(error);

    useEffect(() => {
        if (whens && whens.length > 0) onLoad();
    }, [whens]);

    return <Show whens={whens} isLoading={isLoading} error={error} />;
}

function Public() {
    const { lang } = useLang();

    const [whens, error, isLoading, reload] = useSSRHook<WhenPreview[]>(`/api/when-previews-public?lang=${lang}`);
    useHandleError(error);

    return <Show whens={whens} isLoading={isLoading} error={error} />;
}

type ShowProps = {
    whens: null | WhenPreview[];
    error: null | string | Error;
    isLoading: boolean;
}

function Show({ whens, isLoading, error }: ShowProps) {
    const { dict } = useLang();

    return <>
        {isLoading && <Loading />}
        {error && <p className="error">{dict.whensError}</p>}
        {whens?.length === 0 && <p className="info">{dict.noWhens}</p>}
        {whens && <div className="grid whens">
            {whens.map(p => <WhenPreviewElement key={p._id} when={p} className="grid-item" />)}
        </div>}
    </>
}


function WhenPreviewElement({ when, className }: { when: WhenPreview, className: string }) {
    const { dict } = useLang();

    return <Link href={`/${dict.urlWhen}/${when._id}`} >
        <ThemeDiv theme={when.theme} className={`when-preview preview ${className}`}>
            <h3 className="line-clamp-2">{when.title}</h3>
            <p className="pre-line line-clamp-3">{when.description}</p>
            <ul className="when-options">
                {when.options.filter((_, i) => i < 3).map(o => <li key={o.title} className={`when-option order-${o.order}`}>
                    <VoteResult
                        title={<>
                            <div className="votes">
                                <Votes isMaybeOption={when.isMaybeOption} votes={o} />
                            </div>
                            <When date={o.title} />
                        </>}
                        yes={o.yes}
                        no={o.no}
                        maybe={o.maybe}
                    />
                </li>)}
                {when.options.length > 3 && <li style={{ textAlign: "center" }}>...</li>}
            </ul>
        </ThemeDiv>
    </Link>;
}